import { useEffect } from "react";
import { Link } from "react-router-dom";
import { CheckCircle, ShieldCheck, Phone, EnvelopeSimple, ArrowRight, Clock } from "@phosphor-icons/react";
import Seo from "@/components/Seo";

export default function LeadThanks() {
  useEffect(() => {
    // Meta Pixel: fire the standard "Lead" event so ad campaigns can optimize
    // toward real form submissions rather than raw traffic.
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      window.fbq("track", "Lead", {
        content_name: "SEP Meta Lead Form",
        content_category: "Training Academy",
      });
    }
  }, []);

  return (
    <div data-testid="lead-thanks-page">
      <Seo
        title="Thanks — We'll be in touch · Secrecy Executive Training Academy"
        description="Thanks for submitting your lead form. A member of our training team will reach out within 24 business hours. Review our Privacy Policy for how we handle your contact info."
        path="/lead-thanks"
      />

      {/* Hero confirm */}
      <section className="border-b border-white/10 pt-24 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src="/media/range-a-landscape.jpg" alt="" className="w-full h-full object-cover" aria-hidden="true" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/85 to-transparent" />
        </div>
        <div className="container-x relative">
          <div className="inline-flex items-center gap-2 border border-[#FFD300]/50 bg-[#FFD300]/10 px-3 py-1.5 mb-6" data-testid="lead-thanks-badge">
            <CheckCircle size={16} weight="fill" className="text-[#FFD300]" />
            <span className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#FFD300]">// FORM RECEIVED</span>
          </div>
          <h1 className="font-heading text-5xl md:text-7xl tracking-tighter leading-[0.9] max-w-4xl">
            You're in. <span className="text-[#FFD300]">We'll call.</span>
          </h1>
          <p className="mt-6 text-neutral-300 max-w-2xl text-[17px] leading-relaxed">
            Thanks for reaching out to <strong className="text-white">Secrecy Executive Training Academy</strong>. A member
            of our training team will be in touch within <strong className="text-white">24 business hours</strong> to
            confirm your interest, answer questions, and lock in the right course date for you.
          </p>
        </div>
      </section>

      {/* What happens next */}
      <section className="border-b border-white/10 py-14">
        <div className="container-x">
          <div className="overline mb-6">// WHAT HAPPENS NEXT</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="border border-white/10 p-6 hover:border-[#FFD300]/40 transition-colors" data-testid="lead-thanks-step-1">
              <Clock size={28} weight="duotone" className="text-[#FFD300] mb-4" />
              <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-neutral-500 mb-2">Step 01</div>
              <div className="font-heading text-xl tracking-tight mb-2">Callback within 24 hrs</div>
              <p className="text-neutral-400 text-sm leading-relaxed">
                Expect a call from <span className="font-mono text-[#FFD300]">410-645-0722</span> during business hours
                (Mon–Sat, 9 AM – 7 PM ET). Save the number so it doesn't go to voicemail.
              </p>
            </div>
            <div className="border border-white/10 p-6 hover:border-[#FFD300]/40 transition-colors" data-testid="lead-thanks-step-2">
              <EnvelopeSimple size={28} weight="duotone" className="text-[#FFD300] mb-4" />
              <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-neutral-500 mb-2">Step 02</div>
              <div className="font-heading text-xl tracking-tight mb-2">Course info by email</div>
              <p className="text-neutral-400 text-sm leading-relaxed">
                We'll send the class syllabus, prerequisites, and next available dates to the email address you
                submitted. Check spam if you don't see it within 30 minutes.
              </p>
            </div>
            <div className="border border-white/10 p-6 hover:border-[#FFD300]/40 transition-colors" data-testid="lead-thanks-step-3">
              <ShieldCheck size={28} weight="duotone" className="text-[#FFD300] mb-4" />
              <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-neutral-500 mb-2">Step 03</div>
              <div className="font-heading text-xl tracking-tight mb-2">Lock your seat</div>
              <p className="text-neutral-400 text-sm leading-relaxed">
                Classes are capped at 12 students. Once we confirm the right course for you, we'll send a secure
                checkout link to reserve your seat.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Privacy disclosure — required for Meta compliance */}
      <section className="border-b border-white/10 py-14 bg-white/[0.02]" data-testid="lead-thanks-privacy-block">
        <div className="container-x">
          <div className="grid grid-cols-12 gap-6 items-start">
            <div className="col-span-12 md:col-span-4">
              <div className="overline text-[#FFD300] mb-2">// LEGAL &amp; PRIVACY</div>
              <h2 className="font-heading text-3xl md:text-4xl tracking-tighter leading-tight">
                Your info is <span className="text-[#FFD300]">yours.</span>
              </h2>
            </div>
            <div className="col-span-12 md:col-span-8 space-y-4 text-neutral-300 text-[15px] leading-relaxed">
              <p>
                By submitting a Facebook or Instagram lead form to Secrecy Executive Protection LLC, you consent to
                being contacted by <strong className="text-white">phone, SMS, and email</strong> at the number and
                address you provided — for the sole purpose of scheduling training and answering your questions.
              </p>
              <p>
                We <strong className="text-white">never</strong> sell your data. Message and data rates may apply.
                Reply <span className="font-mono text-[#FFD300]">STOP</span> to any SMS to unsubscribe, or email
                {" "}<a href="mailto:info@seprotection.com" className="link-gold">info@seprotection.com</a> with "UNSUBSCRIBE".
              </p>
              <p className="pt-2">
                <Link
                  to="/privacy"
                  className="inline-flex items-center gap-2 border border-[#FFD300]/50 hover:border-[#FFD300] bg-[#FFD300]/10 hover:bg-[#FFD300]/20 text-[#FFD300] px-5 py-3 transition-colors font-mono text-[11px] tracking-[0.2em] uppercase"
                  data-testid="lead-thanks-privacy-link"
                >
                  <ShieldCheck size={16} weight="duotone" /> Read our Privacy Policy <ArrowRight size={14} />
                </Link>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Fast-track CTAs */}
      <section className="py-14">
        <div className="container-x">
          <div className="overline mb-6">// DON'T WANT TO WAIT?</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <a
              href="tel:+14106450722"
              className="border border-white/10 hover:border-[#FFD300] p-8 flex items-center justify-between group transition-colors"
              data-testid="lead-thanks-cta-call"
            >
              <div>
                <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-neutral-500 mb-2">Call now</div>
                <div className="font-heading text-2xl md:text-3xl tracking-tight">410-645-0722</div>
                <div className="text-neutral-400 text-sm mt-1">Mon–Sat · 9 AM – 7 PM ET</div>
              </div>
              <Phone size={40} weight="duotone" className="text-[#FFD300] group-hover:scale-110 transition-transform" />
            </a>
            <Link
              to="/training"
              className="border border-white/10 hover:border-[#FFD300] p-8 flex items-center justify-between group transition-colors"
              data-testid="lead-thanks-cta-training"
            >
              <div>
                <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-neutral-500 mb-2">Or browse</div>
                <div className="font-heading text-2xl md:text-3xl tracking-tight">The Training Academy</div>
                <div className="text-neutral-400 text-sm mt-1">16 courses · 7 states · Register instantly</div>
              </div>
              <ArrowRight size={40} weight="duotone" className="text-[#FFD300] group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
